import React, { useEffect, useMemo } from "react";
import { fmtDate, cellClass } from "../lib/format";

const _ageText = (seconds) => {
  if (seconds === null || seconds === undefined) return "AGE ?";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
};

export const OptionChainTable = ({ heatmap, onSelectCandidate, selectedCandidate, expFilter, setExpFilter }) => {
  const { current_option, candidates, position, underlying_price } = heatmap || {};

  const expirations = useMemo(() => {
    if (!candidates) return [];
    return [...new Set(candidates.map((c) => c.expiration))].sort();
  }, [candidates]);

  // When the expiration filter changes, keep the same selected strike if possible
  // by switching the selection to the matching strike in the new expiration.
  useEffect(() => {
    if (!candidates || !selectedCandidate) return;
    if (expFilter === "ALL") return;
    if (selectedCandidate.expiration === expFilter) return;
    const match = candidates.find(
      (c) => c.expiration === expFilter && Math.abs(c.strike - selectedCandidate.strike) < 0.001
    );
    if (match) onSelectCandidate(match);
  }, [expFilter, candidates, selectedCandidate, onSelectCandidate]);

  const rows = useMemo(() => {
    if (!candidates) return [];
    const filtered = expFilter === "ALL" ? candidates : candidates.filter((c) => c.expiration === expFilter);
    // Group by expiration → strike asc
    return filtered.sort((a, b) => {
      if (a.expiration !== b.expiration) return a.expiration < b.expiration ? -1 : 1;
      return a.strike - b.strike;
    });
  }, [candidates, expFilter]);

  if (!heatmap) {
    return (
      <div className="panel h-full flex items-center justify-center min-h-[400px]" data-testid="option-chain-empty">
        <div className="text-center max-w-sm p-8">
          <div className="label-cap mb-2">No Position Selected</div>
          <p className="text-sm text-[#A1A1AA]">
            Add a covered call position in the Position Tracker, or select one to see
            color-coded roll candidates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" data-testid="option-chain-panel">
      <div className="panel-h flex-col gap-3 items-stretch">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
          <div>
            <div className="label-cap mb-0.5">Symbol</div>
            <div className="font-bold text-2xl tracking-tight leading-none">{position.symbol}</div>
          </div>
          <div>
            <div className="label-cap mb-0.5">Underlying</div>
            <div className="font-mono text-xl text-white leading-none">${underlying_price.toFixed(2)}</div>
          </div>
          <div>
            <div className="label-cap mb-0.5">Current Strike</div>
            <div className="font-mono text-xl text-[#007AFF] leading-none">
              ${position.strike.toFixed(2)}<span className="text-sm text-[#A1A1AA] ml-1">C</span>
            </div>
          </div>
          <div>
            <div className="label-cap mb-0.5">Expires</div>
            <div className="font-mono text-base text-[#A1A1AA] leading-none">{fmtDate(position.expiration)}</div>
          </div>
          <div className="ml-auto self-start text-right">
            <div className="label-cap">Roll Candidates · Heatmap</div>
            {heatmap?.source && (
              <div
                className="mt-1 text-[9px] font-mono tracking-widest uppercase"
                style={{
                  color:
                    heatmap.source === "live"
                      ? "#00E676"
                      : heatmap.source === "backtest"
                      ? "#BF5AF2"
                      : heatmap.stale
                      ? "#FF3B30"
                      : "#FF9F0A",
                }}
                data-testid="data-source-badge"
              >
                {heatmap.source === "live" ? (
                  <>● LIVE TRADIER</>
                ) : heatmap.source === "backtest" ? (
                  <>● REPLAY</>
                ) : heatmap.stale ? (
                  <>● STALE · {_ageText(heatmap.age_seconds)}</>
                ) : (
                  <>● CACHED · {_ageText(heatmap.age_seconds)} OLD</>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-1 flex-nowrap overflow-x-auto">
          <button
            data-testid="exp-filter-all"
            className="chip chip-sm flex-shrink-0"
            data-active={expFilter === "ALL"}
            onClick={() => setExpFilter("ALL")}
          >
            ALL
          </button>
          {expirations.map((e) => (
            <button
              key={e}
              data-testid={`exp-filter-${e}`}
              className="chip chip-sm flex-shrink-0"
              data-active={expFilter === e}
              onClick={() => setExpFilter(e)}
            >
              {fmtDate(e)}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full grid-lines text-xs" data-testid="option-chain-table">
          <thead>
            <tr className="text-left border-b border-[#3F3F46] label-cap">
              <th className="px-3 py-2.5 font-semibold">Expiration</th>
              <th className="px-3 py-2.5 font-semibold text-right">Strike</th>
              <th className="px-3 py-2.5 font-semibold text-right">DTE</th>
              <th className="px-3 py-2.5 font-semibold text-right">Bid / Ask</th>
              <th className="px-3 py-2.5 font-semibold text-right">Δ Delta</th>
              <th className="px-3 py-2.5 font-semibold text-right">Vol</th>
              <th className="px-3 py-2.5 font-semibold text-right">Net Credit</th>
              <th className="px-3 py-2.5 font-semibold text-right">Credit %</th>
              <th className="px-3 py-2.5 font-semibold"></th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {/* Current option row */}
            <tr className="row-current border-b border-[#27272A]" data-testid="current-option-row">
              <td className="px-3 py-2">{fmtDate(current_option.expiration)}</td>
              <td className="px-3 py-2 text-right">${current_option.strike.toFixed(2)}</td>
              <td className="px-3 py-2 text-right">{current_option.dte}d</td>
              <td className="px-3 py-2 text-right">
                ${current_option.bid.toFixed(2)} / ${current_option.ask.toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right">{current_option.delta.toFixed(2)}</td>
              <td className="px-3 py-2 text-right">{current_option.volume}</td>
              <td className="px-3 py-2 text-right text-[#A1A1AA]">—</td>
              <td className="px-3 py-2 text-right text-[#007AFF]">CURRENT</td>
            </tr>
            {rows.map((c, i) => {
              const cls = cellClass(c.category);
              const isSelected =
                selectedCandidate &&
                selectedCandidate.strike === c.strike &&
                selectedCandidate.expiration === c.expiration;
              const isFirstOfExp = i === 0 || rows[i - 1].expiration !== c.expiration;
              const prev = i > 0 ? rows[i - 1] : null;
              const crossesUnderlying =
                prev &&
                prev.expiration === c.expiration &&
                prev.strike < underlying_price &&
                c.strike >= underlying_price;
              const sameStrike = Math.abs(c.strike - position.strike) < 0.001;
              const earlierDate = c.expiration < position.expiration;
              const laterDate = c.expiration > position.expiration;
              const isCurrentSelf = sameStrike && c.expiration === position.expiration;
              // Arrow points toward where the user's current contract sits:
              //   candidate later than current  → current is earlier → "← cur"
              //   candidate earlier than current → current is later  → "cur →"
              //   same expiration                → just "cur"
              const curTag = isCurrentSelf ? "cur" : laterDate ? "← cur" : earlierDate ? "cur →" : "cur";
              return (
                <tr
                  key={`${c.strike}-${c.expiration}`}
                  data-testid={`candidate-row-${c.strike}-${c.expiration}`}
                  className={`opt-row border-b border-[#27272A] cursor-pointer ${
                    isSelected ? "ring-2 ring-inset ring-[#007AFF]" : ""
                  } ${isCurrentSelf ? "row-current-self" : sameStrike ? "row-same-strike" : ""} ${earlierDate ? "row-earlier-date" : ""} ${crossesUnderlying ? "row-crosses-underlying" : ""}`}
                  onClick={() => onSelectCandidate(c)}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {isFirstOfExp ? fmtDate(c.expiration) : ""}
                  </td>
                  <td className="px-3 py-2 text-right">
                    ${c.strike.toFixed(2)}
                    {isCurrentSelf && (
                      <span
                        data-testid="self-tag"
                        className="ml-1.5 inline-block text-[9px] tracking-wider font-semibold text-[#007AFF] uppercase"
                      >
                        {curTag}
                      </span>
                    )}
                    {sameStrike && !isCurrentSelf && (
                      <span
                        data-testid="same-strike-tag"
                        className="ml-1.5 inline-block text-[9px] tracking-wider font-semibold text-[#007AFF] uppercase whitespace-nowrap"
                      >
                        {curTag}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-[#A1A1AA]">{c.dte}d <span className="text-[9px] text-[#71717A]">W{c.week_n}</span></td>
                  <td className="px-3 py-2 text-right">
                    ${c.bid.toFixed(2)} / ${c.ask.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-[#A1A1AA]">{c.delta.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-[#71717A]">{c.volume}</td>
                  <td className={`px-3 py-2 text-right ${cls}`}>
                    {c.net_credit >= 0 ? "+" : "-"}${Math.abs(c.net_credit).toFixed(2)}
                  </td>
                  <td className={`px-3 py-2 text-right ${cls}`}>
                    {c.credit_pct >= 0 ? "+" : ""}
                    {c.credit_pct.toFixed(2)}%
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.category === "credit_strong" && (
                      <span className="text-[10px] uppercase tracking-wider text-[#00E676]">Strong</span>
                    )}
                    {c.category === "credit_light" && (
                      <span className="text-[10px] uppercase tracking-wider text-[#A6F2C8]">Light</span>
                    )}
                    {c.category === "credit_marginal" && (
                      <span className="text-[10px] uppercase tracking-wider text-[#FFD60A]">Marginal</span>
                    )}
                    {c.category === "debit" && (
                      <span className="text-[10px] uppercase tracking-wider text-[#FF3B30]">Debit</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-[#27272A] flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[#71717A]">
        <span className="label-cap">Legend</span>
        <span className="text-[10px]">Strike ≥ underlying (OTM) · weekly thresholds (N=⌈DTE/7⌉):</span>
        <span className="flex items-center gap-1.5"><span className="cell-credit-strong w-3 h-3 inline-block"></span> ≥ N·1.0%</span>
        <span className="flex items-center gap-1.5"><span className="cell-credit-light w-3 h-3 inline-block"></span> N·0.2 – N·1.0%</span>
        <span className="flex items-center gap-1.5"><span className="cell-credit-marginal w-3 h-3 inline-block"></span> 0 – N·0.2%</span>
        <span className="text-[10px] border-l border-[#27272A] pl-3 ml-1">Strike &lt; underlying (ITM):</span>
        <span className="flex items-center gap-1.5"><span className="cell-credit-marginal w-3 h-3 inline-block"></span> any credit · Yellow</span>
        <span className="flex items-center gap-1.5"><span className="cell-debit w-3 h-3 inline-block"></span> debit · Red</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block border-l-2 border-[#007AFF]"></span> Same strike as current</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block border-r-2 border-[#007AFF]"></span> Earlier expiration than current</span>
      </div>
    </div>
  );
};
